// Клиентская маршрутизация по чанку графа: повторяет правила серверного
// backend/app/routing/service.py::_route (кандидаты старта, привязка финиша к
// ребру, порог кружности 5x, обход реки/обрыва до 10x, статус blocked), но
// считает локально, без запроса на каждый отрезок.
import type { Coordinate, RoutedPath } from "../composables/useRoutingPath";
import { haversineDistanceM } from "./routeLineStyle";
import { nearestEdges, nearestVertices, type GraphIndex } from "./routingGraph";

export const MAX_LEG_M = 50_000;
export const MIN_NETWORK_M = 20;
export const DETOUR_RATIO_LIMIT = 5;
export const DETOUR_RATIO_OBSTACLE_LIMIT = 10;
export const MIN_DETOUR_CHECK_M = 30;
const START_CANDIDATES = 12;
const END_EDGES = 6;

export interface RouteRequest {
  start: Coordinate;
  destination: Coordinate;
  destinationMode?: "network" | "poi";
  snapRadiusM?: number;
}

const hav = (a: Coordinate, b: Coordinate) => haversineDistanceM(a[1], a[0], b[1], b[0]);

export const straight = (status: RoutedPath["status"], req: RouteRequest): RoutedPath => ({
  status,
  geometry: [req.start, req.destination],
  start_attachment: null,
  end_attachment: null,
  connector_distance_m: 0,
  network_distance_m: 0,
  total_distance_m: hav(req.start, req.destination),
});

const segIntersect = (ax: number, ay: number, bx: number, by: number, cx: number, cy: number, dx: number, dy: number): boolean => {
  const o = (px: number, py: number, qx: number, qy: number, rx: number, ry: number) => (qx - px) * (ry - py) - (qy - py) * (rx - px);
  const d1 = o(ax, ay, bx, by, cx, cy);
  const d2 = o(ax, ay, bx, by, dx, dy);
  const d3 = o(cx, cy, dx, dy, ax, ay);
  const d4 = o(cx, cy, dx, dy, bx, by);
  return d1 * d2 < 0 && d3 * d4 < 0;
};

// Пересекает ли прямая a-b реку или обрыв чанка (аналог
// repository.direct_crosses_obstacle на сервере).
export function crossesObstacle(index: GraphIndex, a: Coordinate, b: Coordinate): boolean {
  const minX = Math.min(a[0], b[0]), maxX = Math.max(a[0], b[0]);
  const minY = Math.min(a[1], b[1]), maxY = Math.max(a[1], b[1]);
  for (const ob of index.chunk.obstacles) {
    const p = ob.points;
    for (let i = 0; i + 3 < p.length; i += 2) {
      const cx = p[i], cy = p[i + 1], dx = p[i + 2], dy = p[i + 3];
      if (Math.max(cx, dx) < minX || Math.min(cx, dx) > maxX || Math.max(cy, dy) < minY || Math.min(cy, dy) > maxY) continue;
      if (segIntersect(a[0], a[1], b[0], b[1], cx, cy, dx, dy)) return true;
    }
  }
  return false;
}

// ── Dijkstra: бинарная куча на типизированных массивах, ленивое удаление ──
class MinHeap {
  keys: Float64Array;
  vals: Int32Array;
  size = 0;
  constructor(capacity: number) {
    this.keys = new Float64Array(capacity);
    this.vals = new Int32Array(capacity);
  }
  push(key: number, val: number) {
    let i = this.size++;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.keys[p] <= key) break;
      this.keys[i] = this.keys[p];
      this.vals[i] = this.vals[p];
      i = p;
    }
    this.keys[i] = key;
    this.vals[i] = val;
  }
  popTo(out: { key: number; val: number }) {
    out.key = this.keys[0];
    out.val = this.vals[0];
    const key = this.keys[--this.size];
    const val = this.vals[this.size];
    let i = 0;
    for (;;) {
      let c = 2 * i + 1;
      if (c >= this.size) break;
      if (c + 1 < this.size && this.keys[c + 1] < this.keys[c]) c++;
      if (this.keys[c] >= key) break;
      this.keys[i] = this.keys[c];
      this.vals[i] = this.vals[c];
      i = c;
    }
    this.keys[i] = key;
    this.vals[i] = val;
  }
}

interface Best {
  score: number;
  networkM: number;
  startVertex: number;
  connectorM: number;
  path: number[]; // вершины от старта до конца
  edge: number;
  fromSource: boolean;
  attachment: Coordinate;
}

// Рабочие массивы поиска живут вместе с графом и переиспользуются между отрезками:
// выделять их заново на каждый отрезок дорого (размер ~ числу вершин региона).
interface Workspace {
  dist: Float64Array;
  prev: Int32Array;
  stamp: Uint32Array;
  heap: MinHeap;
  run: number;
}
const workspaces = new WeakMap<GraphIndex, Workspace>();
function workspaceFor(index: GraphIndex): Workspace {
  let ws = workspaces.get(index);
  if (!ws) {
    const V = index.chunk.lon.length;
    ws = { dist: new Float64Array(V), prev: new Int32Array(V), stamp: new Uint32Array(V), heap: new MinHeap(2 * index.chunk.edgeA.length + V + 8), run: 0 };
    workspaces.set(index, ws);
  }
  return ws;
}

// Для тестов/замеров: сколько вершин закрыто в последнем поиске (все кандидаты старта).
let settledCount = 0;
export const lastSearchSettled = (): number => settledCount;

function search(index: GraphIndex, req: RouteRequest): Best | "no_network" | null {
  const { chunk, adjStart, adjTo, edgeLen } = index;
  const targets = nearestEdges(index, req.destination[0], req.destination[1], req.snapRadiusM ?? 100, END_EDGES);
  if (!targets.length) return "no_network";
  const candidates = nearestVertices(index, req.start[0], req.start[1], START_CANDIDATES);
  if (!candidates.length) return "no_network";

  // конечные вершины: у каждого ребра оба конца с дробной длиной до точки привязки
  const endInfo = new Map<number, { partialM: number; edge: number; fromSource: boolean; attachment: Coordinate }[]>();
  for (const t of targets) {
    const len = edgeLen[t.edge];
    for (const [vertex, partialM, fromSource] of [
      [chunk.edgeA[t.edge], len * t.fraction, true],
      [chunk.edgeB[t.edge], len * (1 - t.fraction), false],
    ] as [number, number, boolean][]) {
      const list = endInfo.get(vertex) ?? [];
      list.push({ partialM, edge: t.edge, fromSource, attachment: t.point });
      endInfo.set(vertex, list);
    }
  }

  const ws = workspaceFor(index);
  const { dist, prev, stamp, heap } = ws;
  const top = { key: 0, val: 0 };
  let best: Best | null = null;
  let run = ws.run;
  settledCount = 0;

  for (const cand of candidates) {
    run++;
    if (best && cand.distM >= best.score) continue; // даже нулевая сеть не улучшит лучший счёт
    heap.size = 0;
    dist[cand.vertex] = 0;
    prev[cand.vertex] = -1;
    stamp[cand.vertex] = run;
    heap.push(0, cand.vertex);
    const done = new Set<number>();
    while (heap.size) {
      heap.popTo(top);
      const u = top.val;
      if (top.key > dist[u]) continue;
      // Дальше нет смысла: счёт = коннектор + сеть, а лучший уже найден (best меняется по ходу поиска).
      if (best && top.key + cand.distM >= best.score) break;
      if (!done.has(u)) {
        done.add(u);
        settledCount++;
        const ends = endInfo.get(u);
        if (ends) {
          for (const end of ends) {
            const networkM = dist[u] + end.partialM;
            const score = cand.distM + networkM;
            if (networkM >= MIN_NETWORK_M && (!best || score < best.score)) {
              const path: number[] = [];
              for (let v = u; v !== -1; v = prev[v]) path.push(v);
              path.reverse();
              best = { score, networkM, startVertex: cand.vertex, connectorM: cand.distM, path, edge: end.edge, fromSource: end.fromSource, attachment: end.attachment };
            }
          }
        }
      }
      for (let k = adjStart[u]; k < adjStart[u + 1]; k++) {
        const w = adjTo[k];
        const nd = top.key + edgeLen[index.adjEdge[k]];
        if (stamp[w] !== run || nd < dist[w]) {
          stamp[w] = run;
          dist[w] = nd;
          prev[w] = u;
          heap.push(nd, w);
        }
      }
    }
  }
  ws.run = run;
  return best;
}

export function routeOnGraph(index: GraphIndex, req: RouteRequest): RoutedPath {
  const directM = hav(req.start, req.destination);
  if (directM > MAX_LEG_M) return straight("too_long", req);

  let obstacle: boolean | null = null;
  const crosses = (): boolean => {
    if (directM <= MIN_DETOUR_CHECK_M) return false;
    if (obstacle === null) obstacle = crossesObstacle(index, req.start, req.destination);
    return obstacle;
  };
  const refuseOrFallback = (status: "no_network" | "disconnected"): RoutedPath =>
    crosses() ? straight("blocked", req) : straight(status, req);

  if (!index.chunk.lon.length) return refuseOrFallback("no_network");
  const found = search(index, req);
  if (found === "no_network") return refuseOrFallback("no_network");
  let best = found;
  let limit = directM * DETOUR_RATIO_LIMIT;
  const checked = directM > MIN_DETOUR_CHECK_M;
  if (checked && (best === null || best.score > limit) && crosses()) {
    if (best !== null && best.score <= directM * DETOUR_RATIO_OBSTACLE_LIMIT) limit = directM * DETOUR_RATIO_OBSTACLE_LIMIT;
  }
  if (best === null) return refuseOrFallback("disconnected");
  if (checked && best.score > limit) return refuseOrFallback("disconnected");

  const { chunk } = index;
  const coord = (v: number): Coordinate => [chunk.lon[v], chunk.lat[v]];
  const startPoint = coord(best.startVertex);
  const geometry: Coordinate[] = [req.start];
  const push = (c: Coordinate) => {
    const last = geometry[geometry.length - 1];
    if (c[0] !== last[0] || c[1] !== last[1]) geometry.push(c);
  };
  for (const v of best.path) push(coord(v));
  push(best.attachment);
  let connectorM = hav(req.start, startPoint);
  if ((req.destinationMode ?? "network") === "poi") {
    push(req.destination);
    connectorM += hav(best.attachment, req.destination);
  }
  return {
    status: "routed",
    geometry,
    start_attachment: startPoint,
    end_attachment: best.attachment,
    connector_distance_m: connectorM,
    network_distance_m: best.networkM,
    total_distance_m: connectorM + best.networkM,
  };
}
